import {
  PriceImportRow,
  priceImportRowsEndpointIdentifier,
  priceImportsEndpointIdentifier,
  UpdatePriceImportRowBody,
  useBulkUpdatePriceImportRowsApi,
  useUpdatePriceImportRowApi,
} from 'apis/price-imports';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';

type RowsCache = { data: PriceImportRow[] } | undefined;

/**
 * Row edits with optimistic cache writes and DEFERRED invalidation.
 *
 * Two deliberate deviations from the repo's usual "invalidate in onSuccess":
 *
 * 1. OPTIMISTIC. A 400ms round trip per cell, across a few hundred rows, is an
 *    unusable review screen. The cache is patched immediately and rolled back
 *    on error.
 *
 * 2. DEFERRED INVALIDATION. Refetching on every commit would re-sort and
 *    re-bucket the grid under the operator's cursor mid-edit. The server
 *    response is merged into the cache instead, and the real refetch is fired
 *    only when the operator leaves the run (see flushPendingRefetch). This is
 *    the same fix already shipped in prices/components/top-products-panel.tsx.
 */
export function useImportRowActions(runId: number) {
  const queryClient = useQueryClient();

  const rowsKey = [priceImportRowsEndpointIdentifier, runId];
  const runKey = [priceImportsEndpointIdentifier, runId];

  const patchCache = useCallback(
    (rowId: number, patch: Partial<PriceImportRow>) => {
      queryClient.setQueriesData<RowsCache>(rowsKey, (cache) => {
        if (!cache?.data) return cache;

        return { ...cache, data: cache.data.map((row) => (row.id === rowId ? { ...row, ...patch } : row)) };
      });
    },
    [queryClient, runId]
  );

  const { mutateAsync: updateRow, isLoading: isSavingRow } = useUpdatePriceImportRowApi({
    onMutate: async ({ rowId, body }) => {
      // Stop an in-flight refetch from clobbering the optimistic write.
      await queryClient.cancelQueries(rowsKey);

      const previous = queryClient.getQueryData<RowsCache>(rowsKey);

      patchCache(rowId, {
        ...(body.dealPrice !== undefined ? { dealPrice: body.dealPrice } : {}),
        ...(body.dealUnit !== undefined ? { dealUnit: body.dealUnit } : {}),
        ...(body.status !== undefined ? { status: body.status } : {}),
        ...(body.notes !== undefined ? { notes: body.notes } : {}),
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      const previous = (context as { previous?: RowsCache } | undefined)?.previous;
      if (previous) queryClient.setQueryData(rowsKey, previous);
    },
    onSuccess: (result) => {
      // Merge the server's version -- it re-derives status, the resolved rung
      // and the issue list, none of which the optimistic patch can know.
      if (result?.data) patchCache(result.data.id, result.data);
    },
  });

  const { mutateAsync: bulkUpdate, isLoading: isSavingBulk } = useBulkUpdatePriceImportRowsApi({
    // A bulk action is an explicit, deliberate act -- refetching straight away
    // is what the operator expects, and nothing is mid-edit.
    onSuccess: () => {
      queryClient.invalidateQueries(rowsKey);
      queryClient.invalidateQueries(runKey);
    },
  });

  /** Fire the deferred refetch. Call when leaving the run or the review step. */
  const flushPendingRefetch = useCallback(() => {
    queryClient.invalidateQueries(rowsKey);
    queryClient.invalidateQueries(runKey);
  }, [queryClient, runId]);

  const saveRow = useCallback(
    (rowId: number, body: UpdatePriceImportRowBody) => updateRow({ runId, rowId, body }).catch(() => undefined),
    [updateRow, runId]
  );

  const setRowsStatus = useCallback(
    (rowIds: number[], status: NonNullable<UpdatePriceImportRowBody['status']>) =>
      bulkUpdate({ runId, rowIds, status }).catch(() => undefined),
    [bulkUpdate, runId]
  );

  return { saveRow, setRowsStatus, flushPendingRefetch, isSaving: isSavingRow || isSavingBulk };
}
